import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { signInWithCustomToken, onAuthStateChanged } from "firebase/auth";
import { authPortal } from "../../firebase-portal";

const VERIFY_MAGIC_LINK_URL =
  process.env.REACT_APP_EXCHANGE_PORTAL_INVITE_URL ||
  "https://exchangeportalinvite-774073159618.us-central1.run.app";

export default function InviteGate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [status, setStatus] = useState("init"); // init | verifying | signingIn | done | error
  const [errorMsg, setErrorMsg] = useState("");

  // read expected query params
  const token = useMemo(
    () => params.get("token") || params.get("t") || "",
    [params]
  );
  const orgId = useMemo(() => params.get("org") || "", [params]);

  // keep staff act-as flags (if present) so RequireClient can allow
  const passthrough = useMemo(() => {
    const as = params.get("actAsClient") || params.get("as");
    return as ? `?as=${encodeURIComponent(as)}` : "";
  }, [params]);

  useEffect(() => {
    let cancelled = false;
    let authUnsub = () => {};

    const goInvalid = (msg = "Invalid or expired link.") => {
      if (cancelled) return;
      console.warn("[InviteGate] redirecting to invalid link page:", msg);
      setStatus("error");
      setErrorMsg(msg);
      const orgQS = orgId ? `?org=${encodeURIComponent(orgId)}` : "";
      navigate(`/portal/invite-invalid${orgQS}`, { replace: true });
    };

    if (!token || !orgId) {
      console.warn("[InviteGate] Missing token or orgId in URL params");
      goInvalid("Missing token or org.");
      return;
    }

    (async () => {
      try {
        console.log("[InviteGate] Starting verification", { orgId, tokenLen: token.length });
        setStatus("verifying");

        // 1) Verify the magic link & get custom token
        let data;
        try {
          const resp = await axios.post(
            VERIFY_MAGIC_LINK_URL,
            { orgId, token },
            { timeout: 25000 }
          );
          data = resp.data;
          console.log("[InviteGate] exchangePortalInvite response:", data);
        } catch (verifyErr) {
          if (cancelled) return;
          console.error("[InviteGate] exchangePortalInvite failed:", verifyErr);
          const msg =
            verifyErr?.response?.data?.error ||
            verifyErr?.message ||
            "Could not verify link.";
          goInvalid(msg);
          return;
        }

        if (!data?.ok || !data?.customToken) {
          if (cancelled) return;
          console.warn("[InviteGate] exchange returned non-ok payload:", data);
          goInvalid(data?.error || "Could not verify link.");
          return;
        }

        // 2) Set up auth listener BEFORE sign-in
        setStatus("signingIn");
        console.log("[InviteGate] Setting up onAuthStateChanged listener");
        authUnsub = onAuthStateChanged(authPortal, (user) => {
          if (cancelled) return;
          console.log("[InviteGate] onAuthStateChanged fired; user:", !!user);
          if (user) {
            setStatus("done");
            navigate(`/portal${passthrough}`, { replace: true });
          }
        });

        // 3) Try signing in with the custom token (portal auth instance)
        try {
          console.log("[InviteGate] Calling signInWithCustomToken (portal)");
          await signInWithCustomToken(authPortal, data.customToken);
          console.log("[InviteGate] signInWithCustomToken resolved");
        } catch (authErr) {
          if (cancelled) return;
          console.error("[InviteGate] signInWithCustomToken failed:", authErr);

          // Network-ish errors: show inline message instead of "invalid link"
          const code = authErr?.code || "";
          if (code === "auth/network-request-failed") {
            setStatus("error");
            setErrorMsg(
              "We couldn’t reach the sign-in service. Please check your connection and try the link again."
            );
            return;
          }

          if (code === "auth/too-many-requests") {
            setStatus("error");
            setErrorMsg(
              "Too many sign-in attempts. Please wait a few minutes and try your link again."
            );
            return;
          }

          // Other auth errors likely mean the token is bad or expired
          goInvalid(
            authErr?.message ||
              "We couldn’t sign you in with that link. It may have expired."
          );
          return;
        }

        // 4) Safety net: if listener somehow didn't fire but user is set
        setTimeout(() => {
          if (cancelled) return;
          const current = authPortal.currentUser;
          console.log(
            "[InviteGate] safety check after signIn; currentUser:",
            !!current
          );
          if (current) {
            setStatus("done");
            navigate(`/portal${passthrough}`, { replace: true });
          }
        }, 1000);
      } catch (err) {
        if (cancelled) return;
        console.error("[InviteGate] verify/sign-in outer failed:", err);
        // Treat unknown errors like network or transient issues, not invalid link
        setStatus("error");
        setErrorMsg(
          err?.message ||
            "Something went wrong while signing you in. Please try your link again."
        );
      }
    })();

    return () => {
      cancelled = true;
      if (authUnsub) authUnsub();
    };
  }, [token, orgId, navigate, passthrough]);

  // Minimal progress UI
  const renderBody = () => {
    if (status === "verifying") return <p>Verifying link with the server…</p>;
    if (status === "signingIn") return <p>Signing you in…</p>;
    if (status === "init") return <p>Preparing…</p>;
    if (status === "done") return <p>Redirecting…</p>;
    if (status === "error")
      return (
        <p style={{ color: "#b91c1c", fontWeight: 600 }}>
          {errorMsg || "We couldn’t sign you in with that link."}
        </p>
      );
    return null;
  };

  return (
    <div
      style={{
        padding: 32,
        maxWidth: 520,
        margin: "48px auto",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <h2 style={{ margin: "0 0 12px" }}>Checking your secure link…</h2>
      {renderBody()}
    </div>
  );
}